import { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { addAnonymousFeedback } from "../../services/wisdomServices/growthService";
import "./AddFeedbackButton.css";

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 255;

function AddFeedbackButton() {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [team, setTeam] = useState("");
  const [message, setMessage] = useState("");

  const privacyMessage = "Your feedback is submitted without personal information. Share your honest thoughts, suggestions, or concerns freely, as often as you'd like!";

  const categoryOptions = [
    { label: "Team Culture", value: "team-culture" },
    { label: "Management", value: "management" },
    { label: "Communication", value: "communication" },
    { label: "Process", value: "process" },
    { label: "Work Environment", value: "work-environment" },
    { label: "Other", value: "other" }
  ];

  const severityOptions = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "medium" },
    { label: "High", value: "high" }
  ];

  const sentimentOptions = [
    { label: "Positive", value: "positive" },
    { label: "Constructive", value: "constructive" },
    { label: "Neutral", value: "neutral" }
  ];

  const closeModal = () => {
    setVisible(false);
    setCategory(null);
    setSeverity(null);
    setSentiment(null);
    setTeam("");
    setMessage("");
  };

  const handleSubmit = async () => {
    if (!category || !severity || !sentiment || !team.trim() || !message.trim()) return;

    try {
      setLoading(true);

      await addAnonymousFeedback({
        category,
        severity,
        sentiment,
        team: team.trim(),
        message: message.trim()
      });

      closeModal();
    } catch (error) {
      console.error("Failed to send anonymous feedback:", error);
    } finally {
      setLoading(false);
    }
  };

  const isValid = category && severity && sentiment && team.trim() && message.trim();

  return (
    <>
      <Tooltip target=".anonymous-feedback-trigger" position="bottom" showDelay={200} />
      {visible && (
        <Tooltip
          target=".feedback-privacy-info"
          position="top"
          event="hover"
          showDelay={150}
          hideDelay={200}
          appendTo={document.body}
          autoHide={false}
        />
      )}
      <Button
        type="button"
        icon="pi pi-comment"
        label="Add Anonymous Feedback"
        outlined
        className="summary-kudos-action-button anonymous-feedback-trigger"
        data-pr-tooltip={privacyMessage}
        onClick={() => setVisible(true)}
      />

      <Dialog
        header="Anonymous Feedback"
        visible={visible}
        className="feedback-dialog"
        modal
        draggable={false}
        onHide={closeModal}
      >
        <div className="summary-growth-modal">
          <div className="feedback-privacy-notice">
            <i className="pi pi-shield" />
            <span>Your voice matters. Your identity stays private.</span>
            <i
              className="pi pi-info-circle feedback-privacy-info"
              data-pr-tooltip={privacyMessage}
              tabIndex={0}
              role="button"
              aria-label="Learn more about feedback privacy"
            />
          </div>

          <div className="summary-growth-modal-row">
            <div className="summary-growth-field">
              <label htmlFor="feedback-category">Category</label>
              <Dropdown
                id="feedback-category"
                value={category}
                options={categoryOptions}
                onChange={(e) => setCategory(e.value)}
                placeholder="Select category"
              />
            </div>

            <div className="summary-growth-field">
              <label htmlFor="feedback-severity">Severity</label>
              <Dropdown
                id="feedback-severity"
                value={severity}
                options={severityOptions}
                onChange={(e) => setSeverity(e.value)}
                placeholder="Select severity"
              />
            </div>
          </div>

          <div className="summary-growth-modal-row">
            <div className="summary-growth-field">
              <label htmlFor="feedback-sentiment">Sentiment</label>
              <Dropdown
                id="feedback-sentiment"
                value={sentiment}
                options={sentimentOptions}
                onChange={(e) => setSentiment(e.value)}
                placeholder="Select sentiment"
              />
            </div>

            <div className="summary-growth-field">
              <label htmlFor="feedback-team">Team <span className="char-counter">({MAX_TITLE_LENGTH - team.length})</span></label>
              <InputText
                id="feedback-team"
                value={team}
                maxLength={100}
                onChange={(e) => setTeam(e.target.value)}
                placeholder="Enter team..."
              />
            </div>
          </div>

          <div className="summary-growth-field">
            <label htmlFor="feedback-message">Message <span className="char-counter">({MAX_DESCRIPTION_LENGTH - message.length})</span></label>
            <InputTextarea
              id="feedback-message"
              value={message}
              maxLength={255}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              autoResize
              placeholder="What is working? What isn't working? or What should leadership know that we may not be seeing?"
            />
          </div>

          <div className="summary-growth-modal-actions">
            <Button
              type="button"
              label="Cancel"
              severity="secondary"
              text
              onClick={closeModal}
            />
            <Button
              type="button"
              label="Send Feedback"
              icon="pi pi-send"
              loading={loading}
              disabled={!isValid || loading}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default AddFeedbackButton;